import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext, { OrderSelectionContext } from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import numberDecorator from 'src/decorators/number.decorator';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { CalendarResourceEntity } from 'src/entities/CalendarResource.entity';
import { DeliveryEventEntity } from 'src/entities/DeliveryEvent.entity';
import { RespectType, WeekDayEn } from 'src/entities/planningEntity';
import { AsyncPlanningResponseEntity, PlanningResponseEntity } from 'src/entities/planningResponseEntity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsOperationEntity } from 'src/entities/PlanningsOperation.entity';
import { PlanningsOperationDeliveryByDeliveryIdEntity } from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { TimelineRequestEntity } from 'src/entities/TimelineRequest.entity';
import { useGaragesQuery } from 'src/hooks/useGarages.query';
import { usePlanningRequest } from 'src/hooks/usePlanningRequest';
import { useQueryLatestAlgorithmRequestVersion } from 'src/hooks/useQueryLatestAlgorithmRequestVersion';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningGroups } from 'src/hooks/useQueryPlanningGroups';
import { useQueryPlanningsLegRunStatistics } from 'src/hooks/useQueryPlanningOrderStatistics';
import { PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY, useInfiniteQueryPlanningsDeliveriesWithLegRuns } from 'src/hooks/useQueryPlanningsDeliveries';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsNotAllocReasons } from 'src/hooks/useQueryPlanningsNotAllocReasons';
import { useQueryPlanningsResultsV5 } from 'src/hooks/useQueryPlanningsResults';
import { useQueryPlanningsSuggestions } from 'src/hooks/useQueryPlanningsSuggestions';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useMutationShiftTruckFixedStatusV5 } from 'src/hooks/useQueryShiftTruckFixedStatus';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY, useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { SHIPMENT_ORDERS_QUERY_KEY, useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import { useRollbackRequest } from 'src/hooks/useRollbackRequest';
import { useScenarioPlanningRequestV5 } from 'src/hooks/useScenarioPlanningRequestV5';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';
import { useTariffCalculationMutation } from 'src/hooks/useTariffCalculation.mutation';
import { Operations } from 'src/models/Operations';
import { PlanningsOperationDelivery } from 'src/models/PlanningsOperationGroup.model';
import arrayUtil from 'src/utils/array.util';
import { datetimeUtil } from 'src/utils/datetime.util';
import { convertShipmentOrderLegRunEntitiesToDeliveries } from 'src/utils/planningsDelivery.util';
import { RelaxedRuleValues, RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { TariffCalculationParams } from '../V3Plans/presenters/TariffCalculationDialog';
import { driverEntitiesReducer, planningReducer, planningInitialState, } from '../V3Plans/reducers';

import Presenter from './Presenter';
import { groupEntitiesReducer, operationEntitiesReducer, shipmentOrderLegRunEntityMapReducer } from './Reducer';

type Props = {
  startDate: Date;
  endDate: Date;
  unit: string;
  customInputFields: string[];
}

const Component: FC<Props> = memo(({
  startDate,
  endDate,
  unit,
  customInputFields,
}) => {
  const plansContext = useContext(PlansContext);
  const orderSelection = useContext(OrderSelectionContext);
  const {
    deliveryEntities, truckDisplayStatus, generateRandomString, calculating
  } = plansContext;
  const { selectedOrderIds, orderIds, allocatedOrderIds } = orderSelection;
  const context = useContext(LicenseContext);
  const queryClient = useQueryClient();
  const { enqueueSnackbar, } = useSnackbar();
  const relaxedRules = useMemo(() => RelaxedRuleValues.map((it) => it), []);

  const startOn = datetimeDecorator.toYyyyMmDd(startDate);
  const endOn = datetimeDecorator.toYyyyMmDd(endDate);

  const resetQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['useQueryPlanningsDeliveries'] });
    void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
    void queryClient.invalidateQueries({ queryKey: ['useQueryPlanningsTrucks'] });
    void queryClient.invalidateQueries({ queryKey: ['useQueryPlanningsDrivers'] });
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY]});
    void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY]});
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY]});
  }, [queryClient]);

  const { data: garageData, isLoading: garageDataIsLoading } = useGaragesQuery();
  const { data: truckData, isLoading: trucksIsLoading } = useQueryPlanningsTrucks(startOn, endOn);
  const { data: driverData, } = useQueryPlanningsDrivers(startOn, endOn);
  const { data: latestAlgorithmRequestVersionData } = useQueryLatestAlgorithmRequestVersion(startOn, endOn);

  const DELIVERIES_PAR_PAGE = 10;
  const SHIPMENT_ORDER_LEG_RUNS_PER_PAGE = 200;

  const [deliveryData, setDeliveryData] = useState<PlanningsDeliveryWithLegRunEntity[]>([]);
  const [shipmentOrderLegRunEntityMap, dispatchShipmentOrderLegRunEntityMap] = useReducer(shipmentOrderLegRunEntityMapReducer, null);
  const {
    data: deliveriesInfiniteData,
    isLoading: deliveriesIsLoading,
    hasNextPage: deliveriesHasNextPage,
    fetchNextPage: fetchNextDeliveries,
  } = useInfiniteQueryPlanningsDeliveriesWithLegRuns(startOn, endOn, DELIVERIES_PAR_PAGE);
  const {
    data: shipmentOrderLegRunsInfiniteData,
    isLoading: shipmentOrderLegRunsIsLoading,
    isFetching: shipmentOrderLegRunsIsFetching,
    hasNextPage: shipmentOrderLegRunsHasNextPage,
    fetchNextPage: fetchNextShipmentOrderLegRuns,
  } = useInfiniteQueryShipmentOrderLegRuns(startOn, endOn, SHIPMENT_ORDER_LEG_RUNS_PER_PAGE);

  const { data: planningGroupsData } = useQueryPlanningGroups();
  const { data: planningOrderStatisticsEntity } = useQueryPlanningsLegRunStatistics(startOn, endOn, selectedOrderIds);

  const { normalPlanning, timelinePlanning, assignTruckToTermporaryDelivery, operationBulkResetting } = useMutationPlanningV5();
  const confirmMutation = useMutationShiftTruckFixedStatusV5();
  const unconfirmMutation = useMutationShiftTruckFixedStatusV5();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groupEntities, dispatchGroupEntities] = useReducer(groupEntitiesReducer, []);
  const [driverEntities, dispatchDriverEntities] = useReducer(driverEntitiesReducer, []);
  const [asyncPlanningResponse, setAsyncPlanningResponse] = useState<AsyncPlanningResponseEntity>(null);
  const [deliveryEvents, setDeliveryEvents] = useState<DeliveryEventEntity[]>([]);
  const [resources, setResources] = useState<CalendarResourceEntity[]>([]);
  const [directionSettingDialogIsOpen, setDirectionSettingDialogIsOpen] = useState<boolean>(false);
  const [currentHistoryVersion, setCurrentHistoryVersion] = useState<string>(undefined);
  const [deliveryIdResourceIndex, setDeliveryIdResourceIndex] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    context.setStartEndOn({ startOn, endOn });
    resetQueries();
    dispatchPlanning({
      type: 'updateStartOn',
      payload: startOn
    });
    dispatchPlanning({
      type: 'updateEndOn',
      payload: endOn
    });
  }, [endOn, startOn]);

  useEffect(() => {
    if (!deliveriesInfiniteData || deliveriesIsLoading) {
      return;
    }
    setDeliveryData(deliveriesInfiniteData.pages.flatMap((page) => page.data));
    if (deliveriesHasNextPage) {
      void fetchNextDeliveries();
    }
  }, [deliveriesHasNextPage, deliveriesInfiniteData, deliveriesIsLoading, fetchNextDeliveries]);

  useEffect(() => {
    if (!shipmentOrderLegRunsInfiniteData || calculating) return;

    const entities = shipmentOrderLegRunsInfiniteData.pages.flatMap((page) => page.data);
    const map = new Map<number, ShipmentOrderLegRunForPlanEntity>();
    entities.forEach((it) => {
      map.set(it.id, it);
    });
    dispatchShipmentOrderLegRunEntityMap({
      type: 'set',
      payload: map
    });
  }, [calculating, shipmentOrderLegRunsInfiniteData, selectedOrderIds]);

  /**
   * 割り当て済みの案件IDを取得し、コンテキストに反映する
   * 割り当て済みの案件を未割り当てリストから除外し、割り当て済みリストに追加する
   */
  const updateAllocatedOrderIds = useCallback(() => {
    const deliveryHasOrderIds: number[] = arrayUtil
      .uniq<number>(
        shipmentOrderLegRunsInfiniteData.pages.flatMap((page) => page.data).filter((it) => it.assigned).map((it) => it.id)
      );

    orderSelection.setAllocatedOrderIds(deliveryHasOrderIds);
    if (deliveryHasOrderIds.some((it) => orderIds.includes(it))) {
      orderSelection.removeOrderIds(deliveryHasOrderIds);
    }
    if (deliveryHasOrderIds.some((it) => selectedOrderIds.includes(it))) {
      orderSelection.bulkRemoveSelectedOrderIds(deliveryHasOrderIds);
    }
  }, [shipmentOrderLegRunsInfiniteData, orderIds, selectedOrderIds]);

  useEffect(() => {
    if (!shipmentOrderLegRunsInfiniteData) return;

    updateAllocatedOrderIds();
  }, [shipmentOrderLegRunsInfiniteData]);

  const mergeYoidumi = useCallback((deliveries: PlanningsDeliveryEntity[], responseData: PlanningsDeliveryEntity[]) => {
    const ids = deliveries.map((it) => it.id);
    const extraEntities = responseData.filter((it) => !ids.includes(it.id));
    return [...deliveries, ...extraEntities];
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void = async (requestOrderIds) => {
    enqueueSnackbar('配送計画を削除します');

    try {
      setIsLoading(true);
      await operationBulkResetting.mutateAsync(requestOrderIds, {
        onSuccess: (res: AxiosResponse<PlanningsDeliveryEntity[]>) => {
          orderSelection.removeAllocatedOrderIds(requestOrderIds);

          // 宵積みがあるとリクエストに含まれないものも削除するので、戻り値を受け取り追加する。
          const merged = mergeYoidumi(deliveryEntities, res.data);

          const allocateHistoryEntity: AllocateHistoryEntity = {
            version: generateRandomString(20),
            companyId: context.config?.selected_company_id,
            actAt: new Date(),
            action: '割当部分解除',
            message: `${requestOrderIds.length.toLocaleString()}件の割当をすべて解除しました`,
            deliveryEntities: merged,
          };

          plansContext.addAllocateHistoryEntity(allocateHistoryEntity);
        }
      });

      enqueueSnackbar('配送計画を削除しました');
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as unknown as AxiosError<string>;
        enqueueSnackbar(axiosError.response.data);
      }
      resetQueries();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSummaries = useCallback((entity: PlanningsDeliveryEntity, truck: PlanningsTruckEntity) => {
    const unloadCount = entity.operations.reduce((sum, it) => {
      if (it.action === '積') return sum;

      const position = [it.latitude, it.longitude].join('-');
      return sum.add(position);
    }, new Set()).size;

    const { maxWeight } = entity.operations.reduce((sum, it) => {
      sum.currentWeight += (it.itemTotalWeightForCalculation * (it.action === '積' ? 1 : -1));
      sum.maxWeight = Math.max(sum.maxWeight, sum.currentWeight);
      return sum;
    }, { currentWeight: 0, maxWeight: 0 });
    const weightRatio = numberDecorator.toRoundedRate(maxWeight / truck.maximumLoadingCapacityWeightForCalculation, '') || '- %';

    const { maxVolume } = entity.operations.reduce((sum, it) => {
      sum.currentVolume += (it.itemTotalVolumeMm3 * (it.action === '積' ? 1 : -1));
      sum.maxVolume = Math.max(sum.maxVolume, sum.currentVolume);
      return sum;
    }, { currentVolume: 0, maxVolume: 0 });
    const volumeRatio = numberDecorator.toRoundedRate(maxVolume / truck.loadingPlatformVolumeMm3, '') || '- %';

    let workingRatio = '- %';
    if (entity.operations.length > 0) {
      const wMinutes = entity.workingAvailableDurationSeconds
        ? entity.workingAvailableDurationSeconds / 60
        : datetimeUtil.diffMinutes(new Date(entity.startAt), new Date(entity.endAt));
      let pMinutes = 0;
      if (entity.departureOperation && entity.arrivalOperation) {
        datetimeUtil.diffMinutes(
          new Date(entity.departureOperation.departure_at),
          new Date(entity.arrivalOperation.arrival_at)
        );
      } else {
        const startAt = entity.operations.sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())[0].arrivalAt;
        const endAt = entity.operations.sort((a, b) => new Date(b.departureAt).getTime() - new Date(a.departureAt).getTime())[0].departureAt;
        pMinutes = datetimeUtil.diffMinutes(new Date(startAt), new Date(endAt));
      }
      workingRatio = numberDecorator.toRoundedRate(pMinutes / wMinutes, '');
    }

    return [
      ['納品先数:', unloadCount, '件'].join(''),
      ['積載率(重量, 体積):', [weightRatio, volumeRatio].join(',')].join(' '),
      ['稼働率:', workingRatio].join('')
    ].join(', ');
  }, []);

  // 以下の順に並べたい
  // 1. truck.sortOrder
  // 2. driver.sortOrder
  // 3. truck.id
  // 4. driver.id
  const calculateResourceIndex = useCallback((deliveryId: number, truck: PlanningsTruckEntity, driver: PlanningsDriverEntity) => {
    if (deliveryIdResourceIndex.has(deliveryId)) {
      return deliveryIdResourceIndex.get(deliveryId);
    }
    const resourceIndex = (truck?.sortOrder ?? 9999) * 1_0000_0000_0000 + (driver?.sortOrder ?? 9999) * 1_0000_0000
        + (truck?.id ?? 999999) * 1_0000 + (driver?.id ?? 999999);
    setDeliveryIdResourceIndex((prev) => new Map(prev).set(deliveryId, resourceIndex));
    return resourceIndex;
  }, [deliveryIdResourceIndex]);

  useEffect(() => {
    if (deliveriesIsLoading) {
      return;
    }

    const events: DeliveryEventEntity[] = deliveryEntities.map((datum) => {
      const id = [
        'delivery',
        datum.id
      ].join('-');
      // const title = '';
      const title = [
        [
          datetimeDecorator.toDayAndHourMinutes(new Date(datum.startAt)),
          datetimeDecorator.toDayAndHourMinutes(new Date(datum.endAt))
        ].join(' ～ '),
        datum.returnToBase ? '' : '(帰庫しない)'
      ].join(' ');
      const start = new Date(datum.startAt).toISOString();
      const end = new Date(datum.endAt).toISOString();
      const resourceId = [datum.truckId, datum.driverId].join('-'); // `${datum.id}`;
      const color = '#fff';

      const truck = truckData?.find((t) => t.id === datum.truckId);
      const driver = driverData?.find((d) => d.id === datum.driverId);
      const resourceIndex = calculateResourceIndex(datum.id, truck, driver);

      return {
        id,
        deliveryId: datum.id,
        title,
        start,
        end,
        resourceId,
        resourceIndex,
        backgroundColor: color,
        borderColor: color,
        display: 'background',
        editable: false,
        displayType: 'delivery',
        continuousShiftGroupId: datum.continuousShiftGroupId,
        continuousShiftStartAt: datum.continuousShiftStartAt,
        continuousShiftEndAt: datum.continuousShiftEndAt,
      };
    });

    const resourceMap = events.sort((a, b) => {
      if (a.resourceId === b.resourceId) {
        return new Date(a.start).getTime() - new Date(b.start).getTime();
      }
      return a.resourceId.localeCompare(b.resourceId);
    }).reduce((prev, it) => {
      if (prev.get(it.resourceId)) {
        prev.set(it.resourceId, [...prev.get(it.resourceId), it]);
      } else {
        prev.set(it.resourceId, [it]);
      }
      return prev;
    }, new Map<string, DeliveryEventEntity[]>());

    // 日付の開始時刻（0:00）を設定
    const dayStart = new Date(startDate);
    dayStart.setHours(0, 0, 0, 0);

    // 日付の終了時刻（23:59:59）を設定
    const dayEnd = new Date(endDate);
    dayEnd.setHours(23, 59, 59, 999);

    const gapEvents: DeliveryEventEntity[] = [];
    resourceMap.forEach((evs, resourceId) => {
      const sorted: { start: Date, end: Date }[] = evs.map((it) => {
        if (it.continuousShiftGroupId) {
          return { start: new Date(it.continuousShiftStartAt), end: new Date(it.continuousShiftEndAt) };
        }
        return { start: new Date(it.start), end: new Date(it.end) };
      }).sort((a, b) => a.start.getTime() - b.start.getTime());
      const gaps: { startAt: Date, endAt: Date}[] = [];
      let current = dayStart;
      sorted.forEach((range) => {
        if (current < range.start) {
          gaps.push({ startAt: current, endAt: range.start });
        }
        if (current < range.end) {
          current = range.end;
        }
      });
      if (current < dayEnd) {
        gaps.push({ startAt: current, endAt: dayEnd });
      }
      const color = '#e0e0e0';
      gaps.forEach((it, idx) => {
        gapEvents.push({
          id: `resource-${resourceId}-${idx}`,
          deliveryId: -1,
          title: '',
          start: it.startAt.toISOString(),
          end: it.endAt.toISOString(),
          resourceId,
          resourceIndex: evs[0].resourceIndex,
          backgroundColor: color,
          borderColor: color,
          display: 'background',
          editable: false,
          displayType: 'delivery',
        });
      });
    });

    setDeliveryEvents([...gapEvents, ...events,]);
  }, [calculateResourceIndex, deliveriesIsLoading, deliveryEntities, driverData, endDate, startDate, truckData]);

  const temporaryTruck = {
    id: -1,
    licensePlateValue: '指定なし',
    temporary: true,
    maximumLoadingCapacityWeightForCalculation: 0,
    loadingPlatformVolumeMm3: 0,
    garageId: garageData ? garageData[0]?.id || 0 : 0,
    partnerCompanyId: 0,
    partnerCompanyName: '',
  } as PlanningsTruckEntity;

  const temporaryDriver = {
    id: -1,
    name: '指定なし',
    emailAddress: null,
    phoneNumber: null,
    activationState: null,
    isActive: true,
    color: '#000',
    companyId: 0,
    sortOrder: 1,
    temporary: true,
    defaultTruckId: 1,
  } as PlanningsDriverEntity;

  useEffect(() => {
    if (deliveriesIsLoading || garageDataIsLoading || trucksIsLoading || !driverData) {
      // setResources([]);
      return;
    }

    const deliveryTruckIds = deliveryEntities.map((it) => it.truckId);
    const filteredTruckData: PlanningsTruckEntity[] = truckData.filter((truck) => deliveryTruckIds.includes(truck.id));
    if (deliveryTruckIds.includes(-1)) {
      filteredTruckData.push(temporaryTruck);
    }
    const garageNameMap = new Map<number, string>();
    garageData.forEach((garage) => {
      garageNameMap.set(garage.id, garage.name);
    });
    const truckGarageMap = new Map<number, string>();
    filteredTruckData.forEach((truck) => {
      truckGarageMap.set(truck.id, garageNameMap.get(truck.garageId) || '');
    });

    const res = filteredTruckData.map((truck) => (
      deliveryEntities.filter((delivery) => delivery.truckId === truck.id).map((datum) => {
        let driver: PlanningsDriverEntity = driverData.find((it) => it.id === datum.driverId);
        if (!driver) {
          driver = temporaryDriver;
        }

        const startDateObj = new Date(datum.startAt);
        const endDateObj = new Date(datum.endAt);

        const weightAndVolume = [
          numberDecorator.convertGramToKg(
            truck.maximumLoadingCapacityWeightForCalculation
          ),
          numberDecorator.convertMm3ToM3(
            truck.loadingPlatformVolumeMm3
          )
        ].filter((maybe) => maybe);

        const title = [
          (truck.temporary ? '指定なし' : truck.licensePlateValue),
          (driver.temporary ? '指定なし' : driver.name),
        ].join(' / ');

        const workingTime = [datetimeDecorator.toDayAndHourMinutes(startDateObj), datetimeDecorator.toDayAndHourMinutes(endDateObj)].join(' ～ ');
        const truckName = [(truck.temporary ? '指定なし' : truck.licensePlateValue), weightAndVolume.map((it) => `(${it})`)].join(' ');
        const driverName = [workingTime, (driver.temporary ? '指定なし' : driver.name)].join(' ');
        const summaries = calculateSummaries(datum, truck);
        const garageName = truckGarageMap.get(truck.id) || '拠点なし';
        const id = [datum.truckId, datum.driverId].join('-'); // `${datum.id}`;

        const resourceIndex = calculateResourceIndex(datum.id, truck, driver);

        return {
          id,
          title,
          driverEntity: driver,
          truckEntity: truck,
          truckName,
          driverName,
          garageName,
          summaries,
          workingTime,
          resourceIndex,
          hasOperations: datum.operations.length > 0,
          partnerCompanyId: truck.partnerCompanyId || 0,
          partnerCompanyName: truck.partnerCompanyName || '運送会社 指定なし',
          deliveryId: datum.id,
        };
      })
    )).flat();

    setResources(res);
  }, [calculateSummaries, deliveriesIsLoading, deliveryEntities, driverData, truckData, garageData, planningGroupsData, garageDataIsLoading, trucksIsLoading, calculateResourceIndex]);

  useEffect(() => {
    if (!planningGroupsData || !truckData || !deliveryEntities) return;

    const allTrucks: PlanningsGroupEntity = {
      id: 0,
      name: 'すべて表示する',
      truckIds: truckData.map((it) => it.id),
    };
    const deliveryTruckIds = deliveryEntities.map((it) => it.truckId);
    const filteredGroupData = planningGroupsData.filter((it) => it.truckIds.some((id) => deliveryTruckIds.includes(id)));
    dispatchGroupEntities({
      type: 'set',
      payload: [allTrucks, ...filteredGroupData]
    });
  }, [deliveryEntities, planningGroupsData, truckData]);

  useEffect(() => {
    selectedOrderIds.forEach((it) => {
      if (!orderIds.includes(it) || allocatedOrderIds.includes(it)) {
        orderSelection.removeSelectedOrderIds(it);
      }
    });
  }, [allocatedOrderIds, selectedOrderIds, orderIds]);

  useEffect(() => {
    if (!shipmentOrderLegRunEntityMap) return;

    const orders = Array.from(shipmentOrderLegRunEntityMap.values());
    if (orders.length === 0) return;
    const allOrderIds = orders.map((it) => it.id);
    orderSelection.setOrderIds(allOrderIds.filter((it) => !allocatedOrderIds.includes(it)));
  }, [allocatedOrderIds, shipmentOrderLegRunEntityMap]);

  useEffect(() => {
    if (!shipmentOrderLegRunsIsLoading && !shipmentOrderLegRunsIsFetching && shipmentOrderLegRunsHasNextPage) {
      void fetchNextShipmentOrderLegRuns();
    }
  }, [fetchNextShipmentOrderLegRuns, shipmentOrderLegRunsHasNextPage, shipmentOrderLegRunsIsFetching, shipmentOrderLegRunsIsLoading]);

  const saveTimeline = useCallback((
    operationEntities: PlanningsOperationEntity[],
    emptyDeliveryIds: number[],
    latestAlgorithmVersion: number,
  ) => {
    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: context.config?.selected_company_id,
      actAt: new Date(),
      action: '手動配車',
      message: '手動配車を実行しました',
      deliveryEntities,
    };

    plansContext.addAllocateHistoryEntity(allocateHistoryEntity);

    const timelineEntities: TimelineRequestEntity[] = operationEntities.map((it) => ({
      id: it.id,
      shift_truck_id: it.shiftTruckId,
      order_id: it.orderId,
      arrival_at: new Date(it.arrivalAt),
      departure_at: new Date(it.departureAt),
      action: it.action,
      cycle: it.cycle,
      changed: it.isChanged,
    }));

    plansContext.setCalculating(true);
    timelinePlanning.mutate({
      startOn, endOn, latestAlgorithmRequestVersion: latestAlgorithmVersion, timelineEntities, emptyDeliveryIds,
    }, {
      onSuccess: (data: AxiosResponse<AsyncPlanningResponseEntity>) => {
        if (data.data.status === 'queued') {
          const entity: AsyncPlanningResponseEntity = data.data;
          setAsyncPlanningResponse(entity);
        } else {
          enqueueSnackbar('自動保存に失敗しました。');
          plansContext.setCalculating(false);
        }
      },
      onError: (e: AxiosError<{ message?: string }>) => {
        plansContext.setCalculating(false);
        updateAllocatedOrderIds();
        // 同一案件が複数トラックに紐づく等、API がユーザー向けメッセージを返す場合はそれを表示する。
        // 空文字メッセージのときは既定文言へフォールバックさせるため ?? ではなく || を使う。
        enqueueSnackbar(e.response?.data?.message || `自動保存でエラーが発生しました。${e.message}`, { variant: 'error' });
      },
    });
  }, [context, deliveryEntities, endOn, enqueueSnackbar, generateRandomString, startOn]);

  const { restoreSplitLegRun, restoreSplitLegRuns } = useMutationShipmentOrderLegRun();
  const { cloneShipmentOrder, bulkDelete } = useMutationShipmentOrder();

  const mutateRestoreSplittedOrder: (orderId: number) => void = useCallback((orderId) => {
    if (!window.confirm('分割した案件を復元します。よろしいですか？')) return;

    setIsLoading(true);
    restoreSplitLegRun.mutate(
      orderId,
      {
        onSuccess: () => {
          enqueueSnackbar('分割した案件を元に戻しました。', { variant: 'success' });
          resetQueries();
        },
        onError: () => {
          enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました。');
        },
        onSettled: () => {
          setIsLoading(false);
        }
      }
    );
  }, []);

  const mutateRestoreSplittedOrders: () => void = useCallback(() => {
    if (!window.confirm('分割した案件をすべて復元します。よろしいですか？')) return;

    setIsLoading(true);
    restoreSplitLegRuns.mutate(
      { startOn, endOn },
      {
        onSuccess: () => {
          enqueueSnackbar('分割した案件を元に戻しました。', { variant: 'success' });
          resetQueries();
        },
        onError: () => {
          enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました。');
        },
        onSettled: () => {
          setIsLoading(false);
        }
      }
    );
  }, [endOn, startOn]);

  const mutateDeleteSpecificOrder: (orderId: number) => void = useCallback((orderId) => {
    setIsLoading(true);

    bulkDelete.mutate([orderId]);

    setIsLoading(false);
  }, []);

  const updateDirectionSettingDialogIsOpen = useCallback((val: boolean) => {
    setDirectionSettingDialogIsOpen(val);
  }, []);

  const printOperationDirectionUrl = useCallback(() => {
    if (!deliveryEntities) return '';

    const ids = deliveryEntities !== null ? deliveryEntities.map((it) => it.shiftId) : [];
    const asidePath = `/operation-directions/?ids=${ids.join(',')}`;

    return asidePath;
  }, [deliveryEntities]);

  const addSelectedOrderId = useCallback((id: number) => {
    orderSelection.addSelectedOrderIds(id);
  }, [orderSelection]);

  const removeSelectedOrderId = useCallback((id: number) => {
    orderSelection.removeSelectedOrderIds(id);
  }, [orderSelection]);

  const mutateDeleteOrder: () => void = useCallback(() => {
    if (!selectedOrderIds) return;
    if (!selectedOrderIds.length) return;

    enqueueSnackbar(`${selectedOrderIds.length.toLocaleString()}件の案件を削除します`);
    setIsLoading(true);

    bulkDelete.mutate(
      selectedOrderIds,
      {
        onSuccess: () => {
          orderSelection.bulkRemoveSelectedOrderIds(selectedOrderIds);

          enqueueSnackbar(`${selectedOrderIds.length.toLocaleString()}件の案件を削除しました`);
        },
        onError: () => {
          enqueueSnackbar('案件の削除でエラーが発生しました。');
        },
        onSettled: () => {
          setIsLoading(false);
        }
      }
    );
  }, [selectedOrderIds]);

  const mutateCloneOrder: (shipmentOrderId: number) => void = useCallback((shipmentOrderId) => {
    plansContext.setIsLoading(true);

    try {
      cloneShipmentOrder.mutate(shipmentOrderId);
    } finally {
      plansContext.setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatchDriverEntities({
      type: 'set',
      payload: driverData || []
    });
  }, [driverData]);

  useEffect(() => {
    plansContext.setDeliveryEntities(convertShipmentOrderLegRunEntitiesToDeliveries(deliveryData, shipmentOrderLegRunEntityMap) || []);
  }, [deliveryData, shipmentOrderLegRunEntityMap]);

  useEffect(() => {
    localStorage.setItem('planningOn', startOn);
  }, [startOn]);

  const [planning, dispatchPlanning] = useReducer(planningReducer, planningInitialState);
  useEffect(() => {
    plansContext.setPlanningEntity(planning);
  }, [planning]);

  const { data: planningResultData, error: planningResultError } = useQueryPlanningsResultsV5(asyncPlanningResponse);

  useEffect(() => {
    const loading = asyncPlanningResponse != null;
    plansContext.setCalculating(loading);
  }, [asyncPlanningResponse]);

  const { planningRequest } = usePlanningRequest({
    dispatchPlanning,
    setAsyncPlanningResponse,
    generateRandomString: plansContext.generateRandomString,
    resetQuery: resetQueries,
    normalPlanningV5: normalPlanning
  });

  const { requestScenarioPlanning } = useScenarioPlanningRequestV5({
    dispatchPlanning,
    setAsyncPlanningResponse,
    resetQuery: resetQueries,
  });

  useEffect(() => {
    if (!(planningResultData || planningResultError)) return;

    if (planningResultData) {
      planningResultData.version = 5;
      plansContext.handlePlanningResponse(planningResultData, '自動配車が完了しました', resetQueries);
    } else if (planningResultError) {
      plansContext.handleErrorResponse(planningResultError, resetQueries);
      updateAllocatedOrderIds();
    }
    setAsyncPlanningResponse(null);
    plansContext.refreshRunningEntities();
  }, [planningResultData, planningResultError, resetQueries, plansContext, updateAllocatedOrderIds]);

  const updateBalancedLoading = useCallback((balancedLoading: boolean) => {
    dispatchPlanning({
      type: 'updateBalancedLoading',
      payload: balancedLoading
    });
  }, []);

  const updateIsRespectRecent = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateIsRespectRecent',
      payload: bool
    });
  }, []);

  const updateExcludeToll = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateExcludeToll',
      payload: bool
    });
  }, []);

  const updateRespectType = useCallback((respectType: RespectType) => {
    dispatchPlanning({
      type: 'updateRespectType',
      payload: respectType
    });
  }, []);

  const updateRespectDaysOfWeek = useCallback((days: WeekDayEn[]) => {
    dispatchPlanning({
      type: 'updateRespectDaysOfWeek',
      payload: days
    });
  }, []);

  const updateRespectOn = useCallback((payload: string) => {
    dispatchPlanning({
      type: 'updateRespectOn',
      payload
    });
  }, []);

  const updateIsTimeTableBackward = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateIsTimeTableBackward',
      payload: bool
    });
  }, []);

  const updateIncludeFutureDeliveries = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateIncludeFutureDeliveries',
      payload: bool
    });
  }, []);

  const updateAutoSplitOrders = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateAutoSplitOrders',
      payload: bool
    });
  }, []);

  const updateDontExchangePreroute = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateDontExchangePreroute',
      payload: bool,
    });
  }, []);

  const updateConcurrentAllOrNothing = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateConcurrentAllOrNothing',
      payload: bool,
    });
  }, []);

  const updateActivateIncrementalMaxNumPistons = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateActivateIncrementalMaxNumPistons',
      payload: bool,
    });
  }, []);

  const updateTraceMode = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateTraceMode',
      payload: bool,
    });
  }, []);

  const updateTraceOnly = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateTraceOnly',
      payload: bool,
    });
  }, []);

  const updateTraceCompanyId = useCallback((id: number) => {
    dispatchPlanning({
      type: 'updateTraceCompanyId',
      payload: id,
    });
  }, []);

  const emptyArray = useMemo(() => [], []);
  const selectedRelaxedRules = useMemo(() => (
    planning.selectedRelaxedRules || emptyArray as RelaxedRuleVo[]
  ), [emptyArray, planning.selectedRelaxedRules]);

  const setSelectedRelaxedRules = useCallback((rules: RelaxedRuleVo[]) => {
    dispatchPlanning({
      type: 'updateSelectedRelaxedRules',
      payload: rules
    });
  }, []);

  const setPriorityLargeTrucks = useCallback((priority: number) => {
    dispatchPlanning({
      type: 'updatePriorityLargeTrucks',
      payload: priority
    });
  }, []);

  const updateExpandWorkingStartMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandWorkingStartTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandWorkingEndMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandWorkingEndTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandLoadStartMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandLoadStartTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandLoadEndMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandLoadEndTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandUnloadStartMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandUnloadStartTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandUnloadEndMinutes = useCallback((minutes: number) => {
    dispatchPlanning({
      type: 'updateExpandUnloadEndTime',
      payload: minutes * 60
    });
  }, []);

  const updateExpandMaxVolumeRate = useCallback((rate: number) => {
    dispatchPlanning({
      type: 'updateExpandMaxVolumeRate',
      payload: rate / 100.0
    });
  }, []);

  const updateExpandMaxLoadCapacityRate = useCallback((rate: number) => {
    dispatchPlanning({
      type: 'updateExpandMaxLoadCapacityRate',
      payload: rate / 100.0
    });
  }, []);

  const updateMLSourceType = useCallback((type: number) => {
    dispatchPlanning({
      type: 'updateMLSourceType',
      payload: type,
    });
  }, []);

  const updateMLPlanning = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateMLPlanning',
      payload: bool,
    });
  }, []);

  const updateOptimizeForLoad = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateOptimizeForLoad',
      payload: bool,
    });
  }, []);

  const updateForceNullBase = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateForceNullBase',
      payload: bool,
    });
  }, []);

  const updateTrunkTransportation = useCallback((bool: boolean) => {
    dispatchPlanning({
      type: 'updateTrunkTransportation',
      payload: bool,
    });
  }, []);

  const [operationEntities, dispatchOperationEntities] = useReducer(operationEntitiesReducer, []);
  const { data: notAllocSuggestions } = useQueryPlanningsSuggestions(plansContext.notAllocState?.sessionId); // 未割り当て理由があればサジェストもある
  const { data: notAllocReasonData, isLoading: notAllocReasonDataIsLoading } = useQueryPlanningsNotAllocReasons(plansContext.notAllocState?.sessionId);

  const showNotAllocReasons = useCallback(() => {
    if (!plansContext.notAllocState.sessionId) return;
    if (notAllocReasonDataIsLoading) return;
    if ([truckData, driverData, notAllocReasonData, deliveryEntities].some((maybe) => !maybe)) return;

    const truckOrder = truckData.reduce((map, truck, index) => { map[truck.id] = index; return map; }, {});
    const driverOrder = driverData.reduce((map, driver, index) => { map[driver.id] = index; return map; }, {});
    const notAllocDeliveryIds = notAllocReasonData.map((it) => it.delivery_id);
    const notAllocDeliveries = deliveryEntities.filter((it) => notAllocDeliveryIds.includes(it.id));
    const sorted = notAllocReasonData.sort((a, b) => {
      const deliveryA = notAllocDeliveries.find((it) => it.id === a.delivery_id);
      const deliveryB = notAllocDeliveries.find((it) => it.id === b.delivery_id);
      // 宵積みの場合に翌日の deliveryId が返ってくることがあるため、deliveryId がない場合は 0 として扱う
      if (deliveryA === undefined || deliveryB === undefined) return 0;

      let ret = truckOrder[deliveryA.truckId] - truckOrder[deliveryB.truckId];
      if (ret === 0) {
        ret = driverOrder[deliveryA.driverId] - driverOrder[deliveryB.driverId];
      }
      return ret;
    });
    if (plansContext.notAllocState.disabledOrders.length > 0) {
      plansContext.notAllocState.disabledOrders.forEach((it: number) => {
        sorted.push({
            order_id: it,
            delivery_id: 0,
            message: '有料道路を使用しないと到達できないルートがあります',
        });
      });
    }

    plansContext.setPlanningsNotAllocReasons(sorted);
  }, [deliveryEntities, driverData, notAllocReasonData, notAllocReasonDataIsLoading, plansContext.notAllocReasons, truckData]);

  useEffect(() => {
    if (notAllocReasonDataIsLoading) return;

    showNotAllocReasons();
  }, [notAllocReasonDataIsLoading, showNotAllocReasons]);

  useEffect(() => {
    if (!deliveryEntities) {
      dispatchOperationEntities({ type: 'reset' });
    } else {
      dispatchOperationEntities({
        type: 'set',
        payload: deliveryEntities.flatMap((it) => it.operations),
      });
    }
  }, [deliveryEntities]);

  useEffect(() => {
    plansContext.setLatestAlgorithmRequestVersion(latestAlgorithmRequestVersionData);
  }, [latestAlgorithmRequestVersionData]);

  useEffect(() => {
    if (!operationEntities) return;
    if (!context.config) return;

    const model = new Operations(context.config, operationEntities);

    const tmp = model.createPlanningsOperationDeliveryByDeliveryIdEntity();
    const entity: PlanningsOperationDeliveryByDeliveryIdEntity = deliveryEntities.reduce((acc, delivery) => {
      acc[delivery.id] = tmp[delivery.id] ?? PlanningsOperationDelivery.empty(context.config?.delivery_list_grouping);
      return acc;
    }, {} as PlanningsOperationDeliveryByDeliveryIdEntity);

    plansContext.setPlanningsOperationDeliveryByDeliveryIdEntity(entity);
  }, [operationEntities, context.config, deliveryEntities]);

  const resetCurrentHistoryVersion = useCallback(() => {
    setCurrentHistoryVersion(undefined);
    plansContext.setDeliveryEntities(convertShipmentOrderLegRunEntitiesToDeliveries(deliveryData, shipmentOrderLegRunEntityMap) || []);
  }, [deliveryData, shipmentOrderLegRunEntityMap]);

  const { rollbackRequest } = useRollbackRequest({
    currentHistoryVersion,
    resetCurrentHistoryVersion,
    resetQuery: resetQueries,
    deliveryData: null,
  });

  const assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void = useCallback((entity) => {
    const cacheKey = [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY, startOn, endOn, DELIVERIES_PAR_PAGE];
    const cachedData: InfiniteData<{
      ids: number[];
      total_pages_count: number;
      data: PlanningsDeliveryEntity[];
    }> = queryClient.getQueryData(cacheKey);
    if (cachedData) {
      const newPages = cachedData.pages.map((page) => {
        const data = page.data.map((dt) => ({
          ...dt,
          truckId: entity.truckId && entity.deliveryIds.includes(dt.id) ? entity.truckId : dt.truckId,
          driverId: entity.driverId && entity.deliveryIds.includes(dt.id) ? entity.driverId : dt.driverId,
        }));
        return {
          ...page,
          data,
        };
      });
      queryClient.setQueryData(cacheKey, { ...cachedData, pages: newPages });
    }

    assignTruckToTermporaryDelivery.mutate({
      startOn, endOn, deliveryIds: entity.deliveryIds, truckId: entity.truckId, driverId: entity.driverId, latestAlgorithmRequestVersion: plansContext.latestAlgorithmRequestVersion,
    }, {
      onSuccess: (data: AxiosResponse<PlanningResponseEntity>) => {
        if (data.data.status === 'queued') {
          setAsyncPlanningResponse(data.data);
        } else {
          plansContext.handlePlanningResponse(data.data, '', () => true);
          // 仮車両を作成している場合に truck や driver を取得し直す
          if (data.data?.deliveries?.data) {
            // truck や driver が新しいものだったら取り直す
            const truckIds = data.data.deliveries.data.map((it) => it.truckId);
            const trucks = truckData.filter((it) => truckIds.includes(it.id));
            if (truckIds.length !== trucks.length) {
              void queryClient.invalidateQueries({ queryKey: ['useQueryPlanningsTrucks'] });
            }
            const driverIds = data.data.deliveries.data.map((it) => it.driverId);
            const drivers = driverData.filter((it) => driverIds.includes(it.id));
            if (driverIds.length !== drivers.length) {
              void queryClient.invalidateQueries({ queryKey: ['useQueryPlanningsDrivers'] });
            }
          } else if (entity.truckId === -1) {
            // 万が一データが帰ってこなかったらキャッシュを全部リフレッシュする
            resetQueries();
          }
        }
      },
      onError: (e: AxiosError<{ message: string; }>) => {
        plansContext.handleErrorResponse(e, resetQueries);
      },
    });
  }, [endOn, plansContext, resetQueries, startOn, driverData, truckData]);

  const handleConfirmDeliveries = useCallback((deliveryIds: number[]) => {
    confirmMutation.mutate({
      shift_truck_ids: deliveryIds,
      fixed: true
    }, {
      onSuccess: () => {
        enqueueSnackbar(`${deliveryIds.length}件の配車を確定しました`, { variant: 'success' });
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          const axiosError = error as AxiosError<{ message: string }>;
          enqueueSnackbar(axiosError.response?.data?.message || '配車確定に失敗しました', { variant: 'error' });
        } else {
          enqueueSnackbar('配車確定に失敗しました', { variant: 'error' });
        }
      }
    });
  }, [confirmMutation, enqueueSnackbar]);

  const handleUnconfirmDeliveries = useCallback((deliveryIds: number[]) => {
    unconfirmMutation.mutate({
      shift_truck_ids: deliveryIds,
      fixed: false
    }, {
      onSuccess: () => {
        enqueueSnackbar(`${deliveryIds.length}件の配車確定を解除しました`, { variant: 'success' });
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          const axiosError = error as AxiosError<{ message: string }>;
          enqueueSnackbar(axiosError.response?.data?.message || '配車確定解除に失敗しました', { variant: 'error' });
        } else {
          enqueueSnackbar('配車確定解除に失敗しました', { variant: 'error' });
        }
      },
    });
  }, [unconfirmMutation, enqueueSnackbar]);

  useEffect(() => {
    plansContext.setTruckEntities(truckData);
  }, [truckData]);

  useEffect(() => {
    plansContext.setDriverEntities(driverData);
  }, [driverData]);

  useEffect(() => {
    if (!currentHistoryVersion) return;

    const historyVersionDeliveries = plansContext.allocateHistories?.find(({ version }) => version === currentHistoryVersion)?.deliveryEntities;

    if (!historyVersionDeliveries) return;
    plansContext.setDeliveryEntities(historyVersionDeliveries);
  }, [currentHistoryVersion, plansContext.allocateHistories]);

  const tariffCalculationMutation = useTariffCalculationMutation();

  const handleTariffCalculationExecute = useCallback((params: TariffCalculationParams, onSuccess?: () => void) => {
    tariffCalculationMutation.mutate(params, {
      onSuccess: (data) => {
        const messages: string[] = [];
        if (data.billingUpdatedCount > 0) {
          messages.push(`請求: ${data.billingUpdatedCount}件`);
        }
        if (data.paymentUpdatedCount > 0) {
          messages.push(`支払: ${data.paymentUpdatedCount}件`);
        }
        if (data.skippedCount > 0) {
          messages.push(`スキップ: ${data.skippedCount}件`);
        }
        enqueueSnackbar(`運賃計算が完了しました。${messages.join('、')}`, { variant: 'success' });
        void queryClient.invalidateQueries({
          queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY]
        });
        onSuccess?.();
      },
      onError: (error: unknown) => {
        const axiosError = error as { response?: { data?: { message?: string } } };
        const message = axiosError?.response?.data?.message || '運賃計算に失敗しました。';
        enqueueSnackbar(message, { variant: 'error' });
      },
    });
  }, [tariffCalculationMutation, enqueueSnackbar]);

  return (
    <Presenter
      startDate={startDate}
      endDate={endDate}
      garageData={garageData}
      deliveryEvents={deliveryEvents}
      resources={resources}
      saveTimeline={saveTimeline}
      truckData={truckData}
      mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
      isLoading={isLoading}
      groupEntities={groupEntities}
      mutateDeleteSpecificOrder={mutateDeleteSpecificOrder}
      printOperationDirectionUrl={printOperationDirectionUrl}
      addSelectedOrderId={addSelectedOrderId}
      removeSelectedOrderId={removeSelectedOrderId}
      unit={unit}
      mutateDeleteOrder={mutateDeleteOrder}
      mutateCloneOrder={mutateCloneOrder}
      planningOrderStatisticsEntity={planningOrderStatisticsEntity}
      customInputFields={customInputFields}
      driverEntities={driverEntities}
      deliveryEntities={deliveryEntities}
      shipmentOrderLegRunEntityMap={shipmentOrderLegRunEntityMap}
      directionSettingDialogIsOpen={directionSettingDialogIsOpen}
      updateDirectionSettingDialogIsOpen={updateDirectionSettingDialogIsOpen}
      mutateRestoreSplittedOrder={mutateRestoreSplittedOrder}
      mutateRestoreSplittedOrders={mutateRestoreSplittedOrders}
      truckDisplayStatus={truckDisplayStatus}
      planningRequest={planningRequest}
      updateBalancedLoading={updateBalancedLoading}
      updateIsRespectRecent={updateIsRespectRecent}
      updateExcludeToll={updateExcludeToll}
      updateRespectType={updateRespectType}
      updateRespectDaysOfWeek={updateRespectDaysOfWeek}
      updateRespectOn={updateRespectOn}
      updateIsTimeTableBackward={updateIsTimeTableBackward}
      updateIncludeFutureDeliveries={updateIncludeFutureDeliveries}
      updateAutoSplitOrders={updateAutoSplitOrders}
      updateDontExchangePreroute={updateDontExchangePreroute}
      updateConcurrentAllOrNothing={updateConcurrentAllOrNothing}
      relaxedRules={relaxedRules}
      selectedRelaxedRules={selectedRelaxedRules}
      setSelectedRelaxedRules={setSelectedRelaxedRules}
      setPriorityLargeTrucks={setPriorityLargeTrucks}
      priorityLargeTrucks={planning.priorityLargeTrucks}
      updateExpandWorkingStartMinutes={updateExpandWorkingStartMinutes}
      updateExpandWorkingEndMinutes={updateExpandWorkingEndMinutes}
      updateExpandLoadStartMinutes={updateExpandLoadStartMinutes}
      updateExpandLoadEndMinutes={updateExpandLoadEndMinutes}
      updateExpandUnloadStartMinutes={updateExpandUnloadStartMinutes}
      updateExpandUnloadEndMinutes={updateExpandUnloadEndMinutes}
      updateExpandMaxVolumeRate={updateExpandMaxVolumeRate}
      updateExpandMaxLoadCapacityRate={updateExpandMaxLoadCapacityRate}
      updateMLSourceType={updateMLSourceType}
      updateMLPlanning={updateMLPlanning}
      updateOptimizeForLoad={updateOptimizeForLoad}
      updateForceNullBase={updateForceNullBase}
      updateTrunkTransportation={updateTrunkTransportation}
      notAllocSuggestions={notAllocSuggestions}
      rollbackRequest={rollbackRequest}
      currentHistoryVersion={currentHistoryVersion}
      setCurrentHistoryVersion={setCurrentHistoryVersion}
      resetCurrentHistoryVersion={resetCurrentHistoryVersion}
      requestScenarioPlanning={requestScenarioPlanning}
      assignTruckToTemporaryDeliveryRequest={assignTruckToTemporaryDeliveryRequest}
      handleConfirmDeliveries={handleConfirmDeliveries}
      handleUnconfirmDeliveries={handleUnconfirmDeliveries}
      updateActivateIncrementalMaxNumPistons={updateActivateIncrementalMaxNumPistons}
      updateTraceMode={updateTraceMode}
      updateTraceOnly={updateTraceOnly}
      updateTraceCompanyId={updateTraceCompanyId}
      showLegRunLoading={shipmentOrderLegRunsIsLoading || shipmentOrderLegRunsIsFetching || shipmentOrderLegRunsHasNextPage}
      handleTariffCalculationExecute={handleTariffCalculationExecute}
      tariffCalculationIsExecuting={tariffCalculationMutation.isPending}
    />
  );
});

export default Component;
